import { useEffect, useState } from "react"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import {
  getChinaAccessConfigApi,
  updateChinaAccessConfigApi,
  getUsageConfigApi,
  updateUsageConfigApi,
  ChinaAccessConfig,
} from "../../features/payments/api/payments.api"
import {
  BuildingOffice2Icon,
  BriefcaseIcon,
  UsersIcon,
  HandThumbUpIcon,
  CheckCircleIcon,
  EyeIcon,
  LockClosedIcon,
  ChatBubbleLeftRightIcon,
  MinusIcon,
  PlusIcon,
} from "@heroicons/react/16/solid"

type Accent = {
  iconSoft: string
  toggleOn: string
}

const RED: Accent = { iconSoft: "bg-red-50 text-red-500", toggleOn: "bg-blue-600" }
const EMERALD: Accent = { iconSoft: "bg-emerald-50 text-emerald-500", toggleOn: "bg-blue-600" }

/* ── controls ── */

const Toggle = ({ checked, onChange, accent }: { checked: boolean; onChange: (v: boolean) => void; accent: Accent }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${checked ? accent.toggleOn : "bg-gray-200"}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
  </button>
)

const Stepper = ({ value, onChange }: { value: number; onChange: (v: number) => void }) => (
  <div className="inline-flex items-center border border-gray-200 rounded-lg overflow-hidden flex-shrink-0">
    <button
      type="button"
      onClick={() => onChange(Math.max(0, value - 1))}
      className="w-8 h-9 flex items-center justify-center text-gray-400 hover:bg-gray-50 hover:text-gray-600 active:bg-gray-100 transition-colors"
    >
      <MinusIcon className="w-3.5 h-3.5" />
    </button>
    <input
      type="number"
      min={0}
      value={value}
      onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
      className="w-12 h-9 text-center text-sm font-semibold text-gray-800 border-x border-gray-200 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
    />
    <button
      type="button"
      onClick={() => onChange(value + 1)}
      className="w-8 h-9 flex items-center justify-center text-gray-400 hover:bg-gray-50 hover:text-gray-600 active:bg-gray-100 transition-colors"
    >
      <PlusIcon className="w-3.5 h-3.5" />
    </button>
  </div>
)

const SavedPill = () => (
  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
    <CheckCircleIcon className="w-3.5 h-3.5" />
    Сохранено
  </span>
)

/* ── layout pieces ── */

const CardHeader = ({ icon, iconBg, title, code }: { icon: React.ReactNode; iconBg: string; title: string; code: string }) => (
  <div className="px-5 py-4 border-b bg-gray-50/60 flex items-center gap-2.5">
    <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
      {icon}
    </span>
    <div>
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      <p className="text-xs text-gray-400">Без подписки <span className="font-mono">{code}</span></p>
    </div>
  </div>
)

const SettingRow = ({
  icon,
  accent,
  title,
  description,
  control,
}: {
  icon: React.ReactNode
  accent: Accent
  title: string
  description: string
  control: React.ReactNode
}) => (
  <div className="flex items-center justify-between gap-4 px-5 py-4">
    <div className="flex items-center gap-3 min-w-0">
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${accent.iconSoft}`}>
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-sm text-gray-800">{title}</p>
        <p className="text-xs text-gray-400">{description}</p>
      </div>
    </div>
    {control}
  </div>
)

/* ── China access-config card (own save button) ── */
const ChinaLimitCard = () => {
  const [cfg, setCfg] = useState<ChinaAccessConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    getChinaAccessConfigApi().then((res) => { setCfg(res.data); setLoading(false) })
  }, [])

  const save = async () => {
    if (!cfg) return
    setSaving(true)
    try {
      const res = await updateChinaAccessConfigApi(cfg)
      setCfg(res.data)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <CardHeader icon={<BuildingOffice2Icon className="w-4 h-4 text-red-500" />} iconBg="bg-red-50" title="Китайские предприятия" code="china_access" />

      {loading || !cfg ? (
        <div className="p-5 space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded-lg animate-pulse" />)}
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          <SettingRow
            icon={<EyeIcon className="w-4 h-4" />}
            accent={RED}
            title="Бесплатный лимит просмотра карточек"
            description="Сколько разных фирм можно открыть бесплатно"
            control={<Stepper value={cfg.preview_detail_limit} onChange={(v) => setCfg({ ...cfg, preview_detail_limit: v })} />}
          />
          <SettingRow
            icon={<LockClosedIcon className="w-4 h-4" />}
            accent={RED}
            title="Скрывать контакты без подписки"
            description="Контакты фирмы видны только с подпиской"
            control={<Toggle checked={cfg.hide_contacts_without_subscription} onChange={(v) => setCfg({ ...cfg, hide_contacts_without_subscription: v })} accent={RED} />}
          />
          <SettingRow
            icon={<ChatBubbleLeftRightIcon className="w-4 h-4" />}
            accent={RED}
            title="Скрывать отзывы без подписки"
            description="Отзывы к фирме видны только с подпиской"
            control={<Toggle checked={cfg.hide_reviews_without_subscription} onChange={(v) => setCfg({ ...cfg, hide_reviews_without_subscription: v })} accent={RED} />}
          />

          <div className="flex items-center justify-end gap-3 px-5 py-4">
            {saved && <SavedPill />}
            <button
              disabled={saving}
              onClick={save}
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
            >
              {saving && <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              Сохранить
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/* ── Usage-config row (job_swipe / vacancy_applicant_contacts), each with its own save button ── */
const UsageLimitRow = ({
  usageKey,
  icon,
  title,
  description,
}: {
  usageKey: "job_swipe" | "vacancy_applicant_contacts"
  icon: React.ReactNode
  title: string
  description: string
}) => {
  const [limit, setLimit] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    getUsageConfigApi(usageKey).then((res) => { setLimit(res.data.limit); setLoading(false) })
  }, [usageKey])

  const save = async () => {
    if (limit === null) return
    setSaving(true)
    try {
      const res = await updateUsageConfigApi(usageKey, limit)
      setLimit(res.data.limit)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex items-center justify-between gap-4 px-5 py-4">
      <div className="flex items-center gap-3 min-w-0">
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${EMERALD.iconSoft}`}>{icon}</span>
        <div className="min-w-0">
          <p className="text-sm text-gray-800">{title}</p>
          <p className="text-xs text-gray-400 truncate">{description}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {saved && <SavedPill />}
        {loading || limit === null ? (
          <div className="h-9 w-24 bg-gray-100 rounded-lg animate-pulse" />
        ) : (
          <Stepper value={limit} onChange={setLimit} />
        )}
        <button
          disabled={saving || loading}
          onClick={save}
          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs font-medium px-3 py-2 rounded-lg transition-colors"
        >
          {saving && <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          Сохранить
        </button>
      </div>
    </div>
  )
}

export const LimitsPage = () => (
  <DashboardLayout title="Лимиты">
    <div className="mb-6">
      <h2 className="text-lg font-semibold text-gray-900">Лимиты бесплатного использования</h2>
      <p className="text-sm text-gray-400 mt-0.5">Что доступно бесплатно, пока пользователь не оформил подписку</p>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <ChinaLimitCard />

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <CardHeader icon={<BriefcaseIcon className="w-4 h-4 text-emerald-500" />} iconBg="bg-emerald-50" title="Вакансии" code="job_access" />

        <div className="divide-y divide-gray-50">
          <UsageLimitRow
            usageKey="job_swipe"
            icon={<HandThumbUpIcon className="w-4 h-4" />}
            title="Свайпы / отклики"
            description="Общий лимит на лайк соискателя и отклик на вакансию, pass не тратится"
          />
          <UsageLimitRow
            usageKey="vacancy_applicant_contacts"
            icon={<UsersIcon className="w-4 h-4" />}
            title="Контакты соискателей"
            description="Сколько разных соискателей работодатель откроет с контактами бесплатно"
          />
        </div>
      </div>
    </div>
  </DashboardLayout>
)
